// frontend/src/app/components/truth-set/proposed-facts-panel.tsx
//
// Stage 3 admin panel for PROPOSED FACTS (truth set, part 2; backend
// app/services/truth_set/facts_service.py). A judge pass over the site read's
// pages and the uploaded documents proposes facts. Each one is backed by
// sentences found verbatim on the brand's own sources; a sentence the model
// could not quote exactly never supports a fact.
//
// Internal conflicts come first: the brand's own site giving two values for a
// single-valued attribute (two founding years) has to be resolved before the
// truth set is offered for confirmation (part 3). Nothing on this panel is
// confirmed.

import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown, ChevronUp, ListChecks, Loader2, Play, Plus, RotateCcw, Trash2,
} from "lucide-react";
import { toast } from "sonner";

import {
  addTruthAttribute, cancelExtraction, getFactsOverview, launchExtraction, removeTruthAttribute,
  resumeExtraction, setVertical,
  type FactsOverview,
} from "@/api/truth-set";

const ACCENT = "var(--spectral-violet)";
const POLL_MS = 4000;

const box: React.CSSProperties = {
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 8,
};
const input: React.CSSProperties = {
  width: "100%", padding: "6px 10px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 6, outline: "none",
};
const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const btn = (primary = false, disabled = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6,
  fontSize: "var(--text-xs)", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  border: primary ? "none" : "1px solid var(--border)",
  backgroundColor: primary ? ACCENT : "transparent",
  color: primary ? "white" : "var(--foreground)",
});

function day(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "";
}

const money = (n: number) => `$${n.toFixed(2)}`;

export function ProposedFactsPanel({ customerId }: { customerId: number }) {
  const [data, setData] = useState<FactsOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [showVocab, setShowVocab] = useState(false);
  const [newAttr, setNewAttr] = useState({ key: "", label: "", description: "", cardinality: "one" as "one" | "many", scope: "brand" as "brand" | "vertical" });

  const refresh = useCallback(async () => {
    try {
      setData(await getFactsOverview(customerId));
    } catch (e: any) {
      toast.error(e?.message || "Could not load the proposed facts.");
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const live = data?.live_run ?? null;
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, refresh]);

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) toast.success(done);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div style={{ ...box, padding: 16, marginBottom: 16 }}>
        <span style={muted}><Loader2 className="inline w-3 h-3 animate-spin" /> Loading proposed facts…</span>
      </div>
    );
  }
  if (!data.available) {
    return (
      <div style={{ ...box, padding: 16, marginBottom: 16 }}>
        <span style={muted}>Proposed facts are not available yet: the part 2 truth-set migration has not been applied.</span>
      </div>
    );
  }

  const facts = data.facts || [];
  const conflicts = data.conflicts || [];
  const lastRun = (data.runs || []).find((r) => r.status !== "pending" && r.status !== "running");
  const pendingSources = data.sources_needing_extraction ?? 0;

  return (
    <div style={{ ...box, padding: 16, marginBottom: 16 }}>
      <button
        type="button" onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none",
                 cursor: "pointer", padding: 0, color: "var(--foreground)" }}
      >
        <ListChecks className="w-4 h-4" style={{ color: ACCENT }} />
        <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Proposed facts</span>
        <span style={{ ...muted, marginLeft: 8 }}>
          {facts.length} proposed
          {conflicts.length ? ` · ${conflicts.length} conflict${conflicts.length === 1 ? "" : "s"} on the brand's own site` : ""}
        </span>
        <span style={{ marginLeft: "auto" }}>{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ ...muted, lineHeight: 1.5 }}>
            Read by {data.model} from the site read and the uploaded documents. Every fact is backed by a sentence
            found word for word on the brand's own sources. These are proposals only: nothing is used until a person
            confirms it.
          </p>

          {/* Vertical and vocabulary */}
          <div style={{ ...box, padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>Vertical</span>
              <select style={{ ...input, width: "auto" }} value={data.vertical} disabled={busy}
                      onChange={(e) => act(() => setVertical(customerId, e.target.value), "Vertical saved")}>
                {(data.verticals || []).map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
              </select>
              <span style={muted}>{data.vocabulary?.length ?? 0} attributes</span>
              <button type="button" style={{ ...btn(), marginLeft: "auto" }} onClick={() => setShowVocab(!showVocab)}>
                {showVocab ? "Hide attributes" : "Attributes"}
              </button>
            </div>
            {showVocab && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {(data.vocabulary || []).map((a) => {
                  const custom = (data.custom_attributes || []).find((c) => c.key === a.key);
                  return (
                    <div key={a.key} style={{ display: "flex", gap: 8, fontSize: "var(--text-xs)", alignItems: "baseline" }}>
                      <span style={{ minWidth: 150, fontWeight: 600 }}>{a.label}</span>
                      <span style={{ ...muted, flex: 1 }}>{a.description}</span>
                      <span style={muted}>{a.cardinality === "one" ? "one value" : "several"} · {a.source}</span>
                      {custom && (
                        <button type="button" style={{ ...btn(false, busy), padding: "1px 6px" }} disabled={busy}
                                onClick={() => act(() => removeTruthAttribute(customerId, custom.id), "Attribute removed")}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 8 }}>
                  <input style={input} placeholder="key (wait_time)" value={newAttr.key}
                         onChange={(e) => setNewAttr({ ...newAttr, key: e.target.value })} />
                  <input style={input} placeholder="Label" value={newAttr.label}
                         onChange={(e) => setNewAttr({ ...newAttr, label: e.target.value })} />
                  <select style={input} value={newAttr.cardinality}
                          onChange={(e) => setNewAttr({ ...newAttr, cardinality: e.target.value as "one" | "many" })}>
                    <option value="one">One value</option>
                    <option value="many">Several values</option>
                  </select>
                  <select style={input} value={newAttr.scope}
                          onChange={(e) => setNewAttr({ ...newAttr, scope: e.target.value as "brand" | "vertical" })}>
                    <option value="brand">This brand only</option>
                    <option value="vertical">Every brand in this vertical</option>
                  </select>
                  <input style={{ ...input, gridColumn: "1 / -1" }} placeholder="What the extractor should look for"
                         value={newAttr.description}
                         onChange={(e) => setNewAttr({ ...newAttr, description: e.target.value })} />
                  <div>
                    <button type="button" style={btn(true, busy)} disabled={busy}
                            onClick={() => act(async () => {
                              await addTruthAttribute(customerId, newAttr);
                              setNewAttr({ key: "", label: "", description: "", cardinality: "one", scope: "brand" });
                            }, "Attribute added. Run the extraction again to read it.")}>
                      <Plus className="w-3 h-3" /> Add attribute
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Run */}
          {live ? (
            <div style={{ ...box, padding: 12, borderColor: ACCENT, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: ACCENT }} />
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                {live.cancel_requested ? "Cancelling…" : "Reading sources"}
              </span>
              <span style={muted}>
                {live.sources_done + live.sources_reused + live.sources_failed} of {live.sources_total} sources
                {live.sources_reused ? ` (${live.sources_reused} unchanged)` : ""} · {money(live.cost_usd)} of {money(live.spend_cap_usd)}
              </span>
              <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: "var(--text-xs)" }}>
                <input type="checkbox" checked={live.cancel_requested} disabled={busy}
                       onChange={(e) => act(() => cancelExtraction(live.id, e.target.checked))} />
                Cancel
              </label>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" style={btn(true, busy)} disabled={busy}
                      onClick={() => act(() => launchExtraction(customerId), "Fact extraction started")}>
                <Play className="w-3 h-3" /> {facts.length ? "Propose facts again" : "Propose facts"}
              </button>
              {facts.length > 0 && (
                <button type="button" style={btn(false, busy)} disabled={busy}
                        onClick={() => act(() => launchExtraction(customerId, { force: true }), "Re-reading every source")}>
                  Re-read every source
                </button>
              )}
              <span style={muted}>
                {pendingSources
                  ? `${pendingSources} source${pendingSources === 1 ? "" : "s"} not yet read (new, changed, or the attributes changed)`
                  : "Every current source has been read"}
              </span>
            </div>
          )}

          {lastRun && (
            <div style={muted}>
              Last run {day(lastRun.finished_at || lastRun.created_at)}: {lastRun.status}
              {lastRun.stop_reason === "spend_cap" ? ` (stopped at the ${money(lastRun.spend_cap_usd)} spend cap)` : ""}
              {` · ${lastRun.sources_done} read, ${lastRun.sources_reused} unchanged, ${lastRun.sources_failed} failed · ${money(lastRun.cost_usd)}`}
              {lastRun.stop_reason && lastRun.stop_reason !== "done" && !live && (
                <button type="button" style={{ ...btn(false, busy), marginLeft: 8, padding: "1px 8px" }} disabled={busy}
                        onClick={() => act(() => resumeExtraction(lastRun.id), "Resumed")}>
                  <RotateCcw className="w-3 h-3" /> Resume
                </button>
              )}
              {lastRun.report_json?.failed_sources?.length ? (
                <ul style={{ paddingLeft: 16, listStyle: "disc", marginTop: 4 }}>
                  {lastRun.report_json.failed_sources.map((f) => (
                    <li key={f.item_id} style={{ wordBreak: "break-all" }}>{f.source || `item ${f.item_id}`}: {f.error}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}

          <p style={muted}>
            {facts.length
              ? `${facts.length} proposed${conflicts.length ? `, ${conflicts.length} conflict${conflicts.length === 1 ? "" : "s"} on the brand's own site` : ""}. Decide them in the Truth set below.`
              : "No facts proposed yet."}
          </p>
        </div>
      )}
    </div>
  );
}
